/**
 * Formatting utilities for numbers, currency, and latency
 */

import type { CustomerIdDisplay } from '../types/risk';

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toFixed(0);
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

export const formatLatency = (ns: number): string => {
  if (ns >= 1_000_000) {
    return `${(ns / 1_000_000).toFixed(1)}ms`;
  }
  if (ns >= 1_000) {
    return `${(ns / 1_000).toFixed(1)}μs`;
  }
  return `${ns.toFixed(0)}ns`;
};

export const abbreviateNumber = (num: number): string => {
  const tier = (Math.log10(Math.abs(num)) / 3) | 0;
  if (tier === 0) return num.toString();
  const suffix = ['', 'K', 'M', 'B', 'T'][tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = num / scale;
  return scaled.toFixed(1) + suffix;
};

export const formatCompact = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num);
};

/** Found 2026-10-30: `import.meta.env.VITE_DEMO_MODE === 'true'` (read directly in a
 * component) gets constant-folded by the minifier at build time — Vite inlines
 * `import.meta.env.VITE_DEMO_MODE` as the literal runtime-replaceable placeholder string
 * (`__WOWYRD_DEMO_MODE__`) *before* the container's own entrypoint.sh ever substitutes it, so
 * the comparison is always `false` in the built bundle and the whole demo-mode branch gets
 * dead-code-eliminated. `window.__WOWYRD_RUNTIME_DEMO_MODE__` (set by a `<script>` tag in
 * `index.html`, which is served as-is, never bundled/minified) is a genuine runtime property
 * access a minifier cannot fold away.
 *
 * The property name is deliberately NOT `__WOWYRD_DEMO_MODE__` itself (see index.html's own
 * comment on this, found 2026-10-30 reproducing a separate live-feed bug report): entrypoint.sh's
 * sed is a literal global replace, so a property name containing the exact placeholder text gets
 * mangled by the same substitution as the quoted value. */
export function isDemoMode(): boolean {
  return (
    (window as unknown as { __WOWYRD_RUNTIME_DEMO_MODE__?: string }).__WOWYRD_RUNTIME_DEMO_MODE__ ===
    'true'
  );
}

/** specs/reporting/case-detail-view-and-regulator-export.md — renders the tagged 3-state
 * customer_id shape (CasesQueue + CaseDetail); `t` is passed in rather than this being a hook
 * itself, so it can be called from either component's own `useT()` result. Lives in a plain
 * utility file, not a component file, so exporting it doesn't trip ESLint's
 * react-refresh/only-export-components rule. */
export function renderCustomerId(c: CustomerIdDisplay, t: (key: string) => string): string {
  switch (c.state) {
    case 'known':
      return c.id;
    case 'not_provided':
      return t('cases.customerNotProvided');
    case 'redacted_by_retention':
      return t('cases.customerRedacted');
  }
}
